<template>
  <RouterLink v-if="$props.to" v-bind="$attrs" :to="$props.to" :class="buttonClass" :role="role ?? 'link'">
    <slot />
  </RouterLink>

  <button v-else v-bind="$attrs" :class="buttonClass" :disabled="disabled || loading" :role="role ?? 'button'">
    <slot />
    <LoadingIndicator class="loader" v-if="loading" />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, RouterLinkProps } from 'vue-router';

import LoadingIndicator from '../loading-indicator.vue';
import { ButtonProps } from './button-props.interface';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<ButtonProps & Partial<RouterLinkProps>>(), {
  variant: 'primary',
  appearance: 'primary',
  size: 'large',
  loading: false,
  disabled: false,
});

const buttonClass = computed(() => [
  'button',
  props.variant,
  props.size,
  { loading: props.loading },
  `appearance-${props.appearance}`,
]);
</script>

<style scoped lang="scss">
.button {
  --t-button-color-text: var(--t-color-neutral-950);
  --t-button-color-text-disabled: var(--t-color-neutral-500);
  --t-button-color-border-focus: var(--t-color-neutral-950);

  appearance: none;

  position: relative;

  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: var(--t-border-radius-sm);

  width: auto;
  height: fit-content;
  padding: 0;
  border: none;

  background: transparent;
  color: var(--t-button-color-text);

  font-weight: 600;
  line-height: normal;
  text-decoration: none;
  white-space: nowrap;

  // An icon keeps its size when the row gets tight; without this the label wins and the icon collapses
  > .svg-icon {
    flex-shrink: 0;
  }

  cursor: pointer;

  transition-property: background-color, color, border-color;
  transition-duration: 150ms;
  transition-timing-function: ease-in-out;

  &.appearance-danger {
    --t-button-color-text: var(--t-text-color-danger-primary);
    --t-button-color-border-focus: var(--t-color-red-600);
  }

  &.appearance-success {
    --t-button-color-text: var(--t-color-green-500);
    --t-button-color-border-focus: var(--t-color-green-500);
  }

  &.primary {
    --t-button-color-text: var(--t-color-neutral-100);

    --t-button-color-background: var(--t-color-neutral-950);
    --t-button-color-background-hover: var(--t-color-neutral-900);
    --t-button-color-background-disabled: var(--t-color-neutral-200);

    background-color: var(--t-button-color-background);

    &.appearance-danger {
      --t-button-color-background: var(--t-color-red-600);
      --t-button-color-background-hover: var(--t-color-red-700);
    }

    &.appearance-success {
      --t-button-color-text: var(--t-color-green-500);
      --t-button-color-background: var(--t-color-green-200);
      --t-button-color-background-hover: var(--t-color-green-300);
    }

    &:hover:not(:disabled):not(:focus) {
      background-color: var(--t-button-color-background-hover);
    }

    &:disabled {
      background-color: var(--t-button-color-background-disabled);
    }
  }

  &.secondary {
    --t-button-color-background: var(--t-color-neutral-100);
    --t-button-color-background-hover: var(--t-color-neutral-200);
    --t-button-color-background-disabled: var(--t-color-neutral-100);

    background-color: var(--t-button-color-background);

    &.appearance-danger {
      --t-button-color-background: var(--t-color-red-100);
      --t-button-color-background-hover: var(--t-color-red-200);
    }

    &.appearance-success {
      --t-button-color-text: var(--t-color-green-500);
      --t-button-color-background: var(--t-color-green-200);
      --t-button-color-background-hover: var(--t-color-green-300);
    }

    &:hover:not(:disabled):not(:focus) {
      background-color: var(--t-button-color-background-hover);
    }

    &:disabled {
      background-color: var(--t-button-color-background-disabled);
    }
  }

  &.outline {
    --t-button-color-border: var(--t-color-border-secondary);
    --t-button-color-border-hover: var(--t-color-neutral-600);
    --t-button-color-border-disabled: var(--t-color-neutral-500);

    background-color: var(--t-color-neutral-white);
    border: 1px solid var(--t-button-color-border);

    &.appearance-danger {
      --t-button-color-border: var(--t-color-red-300);
      --t-button-color-border-hover: var(--t-color-red-500);
    }

    &.appearance-success {
      --t-button-color-text: var(--t-color-green-500);
      --t-button-color-background: var(--t-color-green-200);
      --t-button-color-background-hover: var(--t-color-green-300);
    }

    &:hover:not(:disabled):not(:focus) {
      border-color: var(--t-button-color-border-hover);
    }

    &:disabled {
      border-color: var(--t-button-color-border-disabled);
    }
  }

  &.ghost {
    --t-button-color-background: transparent;
    --t-button-color-background-hover: var(--t-color-neutral-100);

    background-color: var(--t-button-color-background);

    &.appearance-danger {
      --t-button-color-background-hover: var(--t-color-red-100);
    }

    &.appearance-success {
      --t-button-color-background-hover: var(--t-color-green-200);
    }

    &:hover:not(:disabled):not(:focus) {
      background-color: transparent;
    }
  }

  &.small {
    height: 2rem;
    padding: 0 0.75rem;
    font: var(--t-typography-label-md-font);
    border-radius: var(--t-border-radius-sm);
    gap: 0.25rem;
  }

  &.medium {
    height: 2.5rem;
    padding: 0 1rem;
    font-size: 0.875rem;
  }

  &.large {
    height: 3rem;
    padding: 0 1.5rem;
    font-size: 1rem;
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--t-button-color-text-disabled);
  }

  &:focus {
    outline: 1px solid #fff;
    box-shadow: 0 0 0 3px var(--t-button-color-border-focus);
  }

  &.loading {
    color: transparent;

    & > * {
      opacity: 0;
    }
  }

  .loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 1;
  }
}
</style>
